import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { BooleanStrict } from './models.generic';
import { EmbeddedToolLightSchema } from './models.tools';

extendZodWithOpenApi(z);

export const GroupMemoriesBySchema = z
  .enum(['tool', 'title'])
  .openapi({ example: 'tool' })
  .default('tool');

// Unfortunate duplication of the above, can't find a way to merge zod enums?
export const ToolGroupMemoriesBySchema = z
  .enum(['default', 'tool', 'title'])
  .openapi({ example: 'default' })
  .default('default');

export type SettingsRangeEnums = z.infer<typeof SettingsRangeEnumsSchema>;
const SettingsRangeEnumsSchema = z
  .enum(['default', 'off', 'low', 'medium', 'high'])
  .optional()
  .default('default');

export type ToolConfigPreferences = z.infer<typeof ToolConfigPreferencesSchema>;
const ToolConfigPreferencesSchema = z.object({
  group_by: ToolGroupMemoriesBySchema,
  ignored: z
    .boolean()
    .optional()
    .default(false)
    .openapi({ description: 'Mute Memories from this tool' }),
  /** Aka how we merge close-in-time memories, ref `MemoryConstructionParamsSchema.mergeWithinMinutes` */
  continuity: SettingsRangeEnumsSchema,
  /** Aka how we hide short memories, ref `MemoryConstructionParamsSchema.minimumGroupDurationMinutes` */
  noise_cancellation: SettingsRangeEnumsSchema,
  naming_strategy: z
    .enum([
      'default',
      'title',
      'tool',
      /**
       * `hide` has been deprecated, and replaced by separate "compact: bool". We need to jump through some hoops to
       * maintain backwards compatibility/to avoid breaking saved configs in `coerceToolConfigSideEffects`
       */
      'hide',
    ])
    .optional()
    .default('default'),
  sticky: z.boolean().optional().default(false).openapi({
    description: 'Sticky Memories from tool in a dedicated lane (forces `dedicated_lane` on)',
    example: false,
  }),
  dedicated_lane: z.boolean().optional().default(false).openapi({
    description: 'Show Memories from this tool in a dedicated lane',
    example: false,
  }),
  compact: BooleanStrict.optional()
    .default(false)
    .describe('Show Memories from this tool without the title'),
  starred: z.boolean().optional().default(false).openapi({
    description: 'Mark memories from this tool as important (starred)',
    example: false,
  }),
});

export type ToolConfig = z.infer<typeof ToolConfigSchema>;
export const ToolConfigSchema = z.object({
  tool_id: z.string().nullable().optional().openapi({ description: 'Tool ID', example: 'github' }),
  tool_raw: z.string().nullable().optional().default(null).openapi({
    description: `Tools that haven't been defined by us, won't have a tool_id, so we use the raw identifier to store configurations`,
    example: 'random.app',
  }),
  preferences: ToolConfigPreferencesSchema,
  tool: EmbeddedToolLightSchema.nullable().optional(),
});

export type ToolConfigDb = z.infer<typeof ToolConfigDbSchema>;
export const ToolConfigDbSchema = ToolConfigSchema.extend({
  id: z
    .string()
    .uuid()
    .readonly()
    .openapi({ description: 'Config ID', example: '00000000-0000-0000-0000-000000000000' }),
  user_id: z
    .string()
    .uuid()
    .openapi({ description: 'User ID', example: '00000000-0000-0000-0000-000000000000' }),
  updated_at: z.string().datetime({ offset: true }).openapi({
    description: 'When the config was last updated',
    example: '2021-01-01T00:00:00.000Z',
  }),
});
